import axios from 'axios';

// Base API configuration
const API_BASE_URL = "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// API Service Class
class ApiService {
  // Categories
  async getCategories() {
    const response = await api.get('/categories/');
    return response.data;
  }

  async getCategory(slug) {
    const response = await api.get(`/categories/${slug}/`);
    return response.data;
  }

  // Destinations
  async getDestinations(params = {}) {
    const response = await api.get('/destinations/', { params });
    return response.data;
  }

  async getDestination(slug) {
    const response = await api.get(`/destinations/${slug}/`);
    return response.data;
  }

  async getFeaturedDestinations() {
    const response = await api.get('/destinations/featured/');
    return response.data;
  }

  // Tours
  async getTours(params = {}) {
    const response = await api.get('/tours/', { params });
    return response.data;
  }

  async getTour(slug) {
    const response = await api.get(`/tours/${slug}/`);
    return response.data;
  }

  async getFeaturedTours() {
    const response = await api.get('/tours/featured/');
    return response.data;
  }

  // Activities
  async getActivities(params = {}) {
    const response = await api.get('/activities/', { params });
    return response.data;
  }

  async getActivity(slug) {
    const response = await api.get(`/activities/${slug}/`);
    return response.data;
  }

  async getFeaturedActivities() {
    const response = await api.get('/activities/featured/');
    return response.data;
  }

  // Resorts
  async getResorts(params = {}) {
    const response = await api.get('/resorts/', { params });
    return response.data;
  }

  async getResort(slug) {
    const response = await api.get(`/resorts/${slug}/`);
    return response.data;
  }

  async getFeaturedResorts() {
    const response = await api.get('/resorts/featured/');
    return response.data;
  }

  // Tour Guides
  async getTourGuides(params = {}) {
    const response = await api.get('/tour-guides/', { params });
    return response.data;
  }

  async getTourGuide(slug) {
    const response = await api.get(`/tour-guides/${slug}/`);
    return response.data;
  }

  // Services
  async getServices(params = {}) {
    const response = await api.get('/services/', { params });
    return response.data;
  }

  async getService(slug) {
    const response = await api.get(`/services/${slug}/`);
    return response.data;
  }

  async getFeaturedServices() {
    const response = await api.get('/services/featured/');
    return response.data;
  }

  // Shop
  async getShopItems(params = {}) {
    const response = await api.get('/shop/', { params });
    return response.data;
  }

  async getShopItem(slug) {
    const response = await api.get(`/shop/${slug}/`);
    return response.data;
  }

  async getFeaturedShopItems() {
    const response = await api.get('/shop/featured/');
    return response.data;
  }

  // Blog
  async getBlogPosts(params = {}) {
    const response = await api.get('/blog/', { params });
    return response.data;
  }

  async getBlogPost(slug) {
    const response = await api.get(`/blog/${slug}/`);
    return response.data;
  }

  async getFeaturedBlogPosts() {
    const response = await api.get('/blog/featured/');
    return response.data;
  }

  // Gallery
  async getGalleryImages(params = {}) {
    const response = await api.get('/gallery/', { params });
    return response.data;
  }

  async getFeaturedGalleryImages() {
    const response = await api.get('/gallery/featured/');
    return response.data;
  }

  // Pricing
  async getPricingPlans(params = {}) {
    const response = await api.get('/pricing/', { params });
    return response.data;
  }

  // FAQs
  async getFAQs(params = {}) {
    const response = await api.get('/faq/', { params });
    return response.data;
  }

  // Contact
  async submitContact(contactData) {
    const response = await api.post('/contact/', contactData);
    return response.data;
  }

  // Reviews
  async getReviews(params = {}) {
    const response = await api.get('/reviews/', { params });
    return response.data;
  }

  async submitReview(reviewData) {
    const response = await api.post('/reviews/create/', reviewData);
    return response.data;
  }

  async getTestimonials(params = {}) {
    const response = await api.get('/testimonials/', { params });
    return response.data;
  }

  async getBrands(params = {}) {
    const response = await api.get('/brands/', { params });
    return response.data;
  }

  // Social Media
  async getSocialMediaLinks(params = {}) {
    const response = await api.get('/social-media-links/', { params });
    return response.data;
  }

  async getSocialMediaPosts(params = {}) {
    const response = await api.get('/social-media-posts/', { params });
    return response.data;
  }

  async getFeaturedSocialMediaPosts() {
    const response = await api.get('/social-media-posts/featured/');
    return response.data;
  }

  // Search
  async searchAll(query) {
    const response = await api.get('/search/', { params: { q: query } });
    return response.data;
  }

  // Dashboard Stats
  async getDashboardStats() {
    const response = await api.get('/stats/');
    return response.data;
  }

  // About Section
  async getAboutSection() {
    const response = await api.get('/about-section/');
    return response.data;
  }

  // Hero Slides
  async getHeroSlides() {
    const response = await api.get('/hero-slides/');
    return response.data;
  }

  // Booking Form Data
  async getServiceTypes() {
    const response = await api.get('/service-types/');
    return response.data;
  }

  async getAccommodationLocations(params = {}) {
    const response = await api.get('/accommodation-locations/', { params });
    return response.data;
  }

  async getCarTypes() {
    const response = await api.get('/car-types/');
    return response.data;
  }

  async getPackageTypes() {
    const response = await api.get('/package-types/');
    return response.data;
  }

  // Airbnb-specific methods
  async getAccommodations(params = {}) {
    const response = await api.get('/accommodations/', { params });
    return response.data;
  }

  async getAccommodation(slug) {
    const response = await api.get(`/accommodations/${slug}/`);
    return response.data;
  }

  async searchAccommodations(params = {}) {
    const response = await api.get('/accommodations/search/', { params });
    return response.data;
  }

  async getFeaturedAccommodations() {
    const response = await api.get('/accommodations/featured/');
    return response.data;
  }

  async getAccommodationTypes() {
    const response = await api.get('/accommodation-types/');
    return response.data;
  }

  async getAmenities(params = {}) {
    const response = await api.get('/amenities/', { params });
    return response.data;
  }

  // Accommodation Bookings
  async createAccommodationBooking(bookingData) {
    const response = await api.post('/accommodation-bookings/create/', bookingData);
    return response.data;
  }

  async getAccommodationBookings(params = {}) {
    const response = await api.get('/accommodation-bookings/', { params });
    return response.data;
  }

  async getAccommodationBooking(bookingReference) {
    const response = await api.get(`/accommodation-bookings/${bookingReference}/`);
    return response.data;
  }

  // Airbnb Page Specific Content
  async getAirbnbHeroSlides() {
    const response = await api.get('/airbnb/hero-slides/');
    return response.data;
  }

  async getAirbnbAboutSection() {
    const response = await api.get('/airbnb/about/');
    return response.data;
  }

  async getAirbnbPropertySection() {
    const response = await api.get('/airbnb/property-section/');
    return response.data;
  }

  async getAirbnbExploreSection() {
    const response = await api.get('/airbnb/explore/');
    return response.data;
  }

  async getAirbnbTeamSection() {
    const response = await api.get('/airbnb/team/');
    return response.data;
  }

  async getAirbnbTestimonialSection() {
    const response = await api.get('/airbnb/testimonial-section/');
    return response.data;
  }

  async getAirbnbContactSection() {
    const response = await api.get('/airbnb/contact/');
    return response.data;
  }

  async getAirbnbTestimonials(params = {}) {
    const response = await api.get('/airbnb/testimonials/', { params });
    return response.data;
  }

  async getFeaturedAirbnbTestimonials() {
    const response = await api.get('/airbnb/testimonials/featured/');
    return response.data;
  }

  async getAirbnbBlogSection() {
    const response = await api.get('/airbnb/blog-section/');
    return response.data;
  }

  async getAirbnbBlogPosts(params = {}) {
    const response = await api.get('/airbnb/blog/', { params });
    return response.data;
  }

  async getAirbnbBlogPost(slug) {
    const response = await api.get(`/airbnb/blog/${slug}/`);
    return response.data;
  }

  async getFeaturedAirbnbBlogPosts() {
    const response = await api.get('/airbnb/blog/featured/');
    return response.data;
  }

  // AirTicket API methods (HomeThree/home-agency page)
  async getAirTicketHeroSlides() {
    const response = await api.get('/airticket/hero-slides/');
    // Return the results array from paginated response
    return response.data.results || response.data;
  }

  async getAirTicketDestinations(params = {}) {
    const response = await api.get('/airticket/destinations/', { params });
    return response.data.results || response.data;
  }

  async getFeaturedAirTicketDestinations() {
    const response = await api.get('/airticket/destinations/featured/');
    return response.data;
  }

  async getAirTicketDestination(slug) {
    const response = await api.get(`/airticket/destinations/${slug}/`);
    return response.data;
  }

  async getAirTicketCategories() {
    const response = await api.get('/airticket/categories/');
    return response.data.results || response.data;
  }

  async getAirTicketCategory(slug) {
    const response = await api.get(`/airticket/categories/${slug}/`);
    return response.data;
  }

  async getAirTicketAboutSection() {
    const response = await api.get('/airticket/about/');
    // Return the first result from paginated response or the data if not paginated
    return response.data.results?.[0] || response.data;
  }

  async getAirTicketTours(params = {}) {
    const response = await api.get('/airticket/tours/', { params });
    return response.data;
  }

  async getFeaturedAirTicketTours() {
    const response = await api.get('/airticket/tours/featured/');
    return response.data;
  }

  async getAirTicketTour(slug) {
    const response = await api.get(`/airticket/tours/${slug}/`);
    return response.data;
  }

  async getAirTicketGallery(params = {}) {
    const response = await api.get('/airticket/gallery/', { params });
    return response.data;
  }

  async getFeaturedAirTicketGallery() {
    const response = await api.get('/airticket/gallery/featured/');
    return response.data;
  }

  async getAirTicketAgents(params = {}) {
    const response = await api.get('/airticket/agents/', { params });
    return response.data;
  }

  async getFeaturedAirTicketAgents() {
    const response = await api.get('/airticket/agents/featured/');
    return response.data;
  }

  async getAirTicketAgent(id) {
    const response = await api.get(`/airticket/agents/${id}/`);
    return response.data;
  }

  async getAirTicketServices(params = {}) {
    const response = await api.get('/airticket/services/', { params });
    return response.data;
  }

  async getAirTicketContact() {
    const response = await api.get('/airticket/contact/');
    return response.data;
  }

  async getAirTicketTestimonials(params = {}) {
    const response = await api.get('/airticket/testimonials/', { params });
    return response.data;
  }

  async getFeaturedAirTicketTestimonials() {
    const response = await api.get('/airticket/testimonials/featured/');
    return response.data;
  }

  async getAirTicketBrands(params = {}) {
    const response = await api.get('/airticket/brands/', { params });
    return response.data;
  }

  async getAirTicketBlogPosts(params = {}) {
    const response = await api.get('/airticket/blog/', { params });
    return response.data;
  }

  async getFeaturedAirTicketBlogPosts() {
    const response = await api.get('/airticket/blog/featured/');
    return response.data;
  }

  async getAirTicketBlogPost(slug) {
    const response = await api.get(`/airticket/blog/${slug}/`);
    return response.data;
  }

  // ================================
  // TOUR API METHODS (for home-tour page)
  // ================================
  
  // Tour Page Data - Single endpoint for all tour page data
  async getTourPageData() {
    const response = await api.get('/tour/page-data/');
    return response.data;
  }

  // Tour Hero Slides
  async getTourHeroSlides() {
    const response = await api.get('/tour/hero-slides/');
    return response.data;
  }

  async getActiveTourHeroSlides() {
    const response = await api.get('/tour/hero/');
    return response.data;
  }

  // Tour Destinations
  async getTourDestinations(params = {}) {
    const response = await api.get('/tour/destinations/', { params });
    return response.data;
  }

  async getTourDestination(id) {
    const response = await api.get(`/tour/destinations/${id}/`);
    return response.data;
  }

  async getFeaturedTourDestinations() {
    const response = await api.get('/tour/destinations/featured/');
    return response.data;
  }

  async getPopularTourDestinations() {
    const response = await api.get('/tour/destinations/popular/');
    return response.data;
  }

  // Tour Categories
  async getTourCategories(params = {}) {
    const response = await api.get('/tour/categories/', { params });
    return response.data;
  }

  async getTourCategory(id) {
    const response = await api.get(`/tour/categories/${id}/`);
    return response.data;
  }

  async getTourCategoriesWithTours() {
    const response = await api.get('/tour/categories/with-tours/');
    return response.data;
  }

  // Tour About Section
  async getTourAboutSection() {
    const response = await api.get('/tour/about/');
    return response.data;
  }

  // Tour Gallery
  async getTourGallery(params = {}) {
    const response = await api.get('/tour/gallery/', { params });
    return response.data;
  }

  async getFeaturedTourGallery() {
    const response = await api.get('/tour/gallery/featured/');
    return response.data;
  }

  async getTourGalleryByDestination(destinationId) {
    const response = await api.get(`/tour/gallery/by-destination/?destination_id=${destinationId}`);
    return response.data;
  }

  // Tour Guide Profiles
  async getTourGuideProfiles(params = {}) {
    const response = await api.get('/tour/guides/', { params });
    return response.data;
  }

  async getTourGuideProfile(id) {
    const response = await api.get(`/tour/guides/${id}/`);
    return response.data;
  }

  async getFeaturedTourGuides() {
    const response = await api.get('/tour/guides/featured/');
    return response.data;
  }

  async getTourGuidesByExperience() {
    const response = await api.get('/tour/guides/by-experience/');
    return response.data;
  }

  // Tour Services
  async getTourServices(params = {}) {
    const response = await api.get('/tour/services/', { params });
    return response.data;
  }

  async getTourService(id) {
    const response = await api.get(`/tour/services/${id}/`);
    return response.data;
  }

  async getTourServicesByPriceRange(minPrice, maxPrice) {
    const params = {};
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    const response = await api.get('/tour/services/by-price/', { params });
    return response.data;
  }

  // Tour Testimonials
  async getTourTestimonials(params = {}) {
    const response = await api.get('/tour/testimonials/', { params });
    return response.data;
  }

  async getTourTestimonial(id) {
    const response = await api.get(`/tour/testimonials/${id}/`);
    return response.data;
  }

  async getFeaturedTourTestimonials() {
    const response = await api.get('/tour/testimonials/featured/');
    return response.data;
  }

  async getHighRatedTourTestimonials() {
    const response = await api.get('/tour/testimonials/high-rated/');
    return response.data;
  }

  async getRecentTourTestimonials() {
    const response = await api.get('/tour/testimonials/recent/');
    return response.data;
  }

  // Tour Search
  async searchTours(params = {}) {
    const response = await api.get('/tour/search/', { params });
    return response.data;
  }
}

// Create and export a single instance
const apiService = new ApiService();
export default apiService;

// Also export the axios instance for custom requests
export { api };

// Helper functions for common operations
export const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price);
};

export const formatRating = (rating) => {
  return parseFloat(rating).toFixed(1);
};

export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `https://dzsoftech.net/tours${imagePath}`;
};

export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    console.error('API Error:', error.response.data);
    return error.response.data.message || 'Server error occurred';
  } else if (error.request) {
    // Request was made but no response received
    console.error('Network Error:', error.request);
    return 'Network error - please check your connection';
  } else {
    // Something else happened
    console.error('Error:', error.message);
    return 'An unexpected error occurred';
  }
};

// Get About Page data (for dedicated About Us page)
export const getAboutPageData = () => api.get('/about-page/');

